/* eslint-disable indent */
import styled from "styled-components";

import { AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectTrigger } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs } from "@/components/ui/tabs";
import { device } from "@/utils/sizes";
import { colourScheme } from "@/utils/styling/colours";

export const StyledCard = styled(Card)<{ $hasResponses: boolean }>`
    flex-basis: 100%;
    width: 100%;
    margin: 0;
    background: linear-gradient(
        to bottom right,
        ${colourScheme.darkGradientStart},
        ${colourScheme.darkGradientEnd}
    );
    color: ${colourScheme.white};
    box-shadow:
        0 4px 6px ${colourScheme.shadow},
        0 5px 15px ${colourScheme.shadow};
    border: 1px solid ${colourScheme.primaryLight};
    border-radius: 0.75rem;
    overflow: hidden;
    transition: all 0.5s ease-in-out; // Smooth transitions for width changes

    ${device.xl} {
        flex-basis: ${(props) => (props.$hasResponses ? "50%" : "48rem")};
    }
    height: auto;
    align-self: flex-start;
`;

export const StyledCardHeader = styled(CardHeader)`
    border-bottom: 1px solid ${colourScheme.primaryLight};
    padding: 1rem;
`;

export const StyledCardTitle = styled(CardTitle)`
    font-size: 1.5rem;
    font-weight: bold;
    background: linear-gradient(
        to right,
        ${colourScheme.secondaryLight},
        ${colourScheme.secondary}
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;

    ${device.md} {
        font-size: 1.875rem;
    }
`;

export const StyledCardDescription = styled(CardDescription)`
    color: ${colourScheme.greyLight};
    font-size: 0.875rem;

    ${device.md} {
        font-size: 1rem;
    }
`;

export const StyledCardContent = styled(CardContent)`
    padding: 1rem;
`;

export const StyledCardFooter = styled(CardFooter)`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 1rem;

    ${device.md} {
        flex-direction: row;
    }
`;

export const StyledForm = styled.form`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

export const StyledRadioGroup = styled(RadioGroup)`
    display: flex;
    justify-content: center;
    gap: 1rem;
    margin-top: 1rem;

    ${device.md} {
        margin-top: 0;
        justify-content: flex-start;
    }
`;

// RadioGroup
export const StyledRadioGroupItem = styled(RadioGroupItem)`
    border-color: ${colourScheme.white};
    color: ${colourScheme.white};
`;

export const StyledGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;
    align-items: center;

    ${device.md} {
        grid-template-columns: 1fr 1fr;
    }
`;

export const StyledButton = styled(Button)`
    width: 100%;
    ${device.md} {
        width: auto;
    }
`;

export const StyledOutlineButton = styled(StyledButton)`
    background: linear-gradient(
        to right,
        ${colourScheme.primaryLight},
        ${colourScheme.primary}
    );
    color: ${colourScheme.white};
    border: 1px solid ${colourScheme.primaryLight};

    &:hover {
        background: linear-gradient(
            to right,
            ${colourScheme.primary},
            ${colourScheme.primaryLight}
        );
    }
`;

export const FlexContainer = styled.div`
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    width: 100%;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;

    ${device.md} {
        flex-direction: row;
    }
`;

export const IconTextContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: ${colourScheme.white};
`;

export const StyledSubmitButton = styled(StyledButton)<{ isPremium: boolean }>`
    width: 100%;
    transition: all 0.2s ease-in-out;
    margin-left: 1rem;

    background: ${({ isPremium }) =>
        isPremium
            ? "linear-gradient(to right, #37C296, #61E0B1)"
            : "linear-gradient(to right, #4F80CC, #7AA8E3)"};

    &:hover {
        background: ${({ isPremium }) =>
            isPremium
                ? "linear-gradient(to right, #2CA478, #4FD1A0)"
                : "linear-gradient(to right, #456CAB, #6B9AD6)"};
    }

    color: #e8eaf0; /* Text color */
    ${device.md} {
        width: auto;
    }
`;

export const StyledInput = styled(Input)`
    background-color: ${colourScheme.primary};
    color: ${colourScheme.white};
    border: 1px solid ${colourScheme.primaryLight};
    padding: 0.5rem;
    border-radius: 0.375rem;
    width: 100%;

    &:focus {
        border-color: ${colourScheme.secondary};
    }
`;

export const StyledSelect = styled(Select)`
    .select-trigger {
        background-color: ${colourScheme.primary};
        color: ${colourScheme.white};
        border-color: ${colourScheme.primaryLight};

        &:hover {
            background-color: ${colourScheme.primaryLight};
        }
    }

    .select-content {
        background-color: ${colourScheme.primary};
        color: ${colourScheme.white};
    }
`;

export const StyledSelectTrigger = styled(SelectTrigger)`
    background-color: #1a1c34;
    color: #e8eaf0;
    border: 1px solid #2c2e52;
    padding: 0.5rem;
    border-radius: 0.375rem;
    width: 100%;
    transition: background-color 0.2s;
    &:hover {
        background-color: #2c2e52;
    }
`;

export const SelectContentStyled = styled.div`
    background-color: #1a1c34;
    color: #e8eaf0;
`;

export const StyledAccordionTrigger = styled(AccordionTrigger)`
    color: ${colourScheme.white};

    &:hover {
        color: ${colourScheme.secondaryLight};
    }
`;

export const StyledCollapsibleTrigger = styled(CollapsibleTrigger)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0.5rem;
    background: linear-gradient(
        to right,
        ${colourScheme.primaryLight},
        ${colourScheme.primary}
    );
    color: ${colourScheme.white};
    border-radius: 0.375rem;

    &:hover {
        background: linear-gradient(
            to right,
            ${colourScheme.primary},
            ${colourScheme.primaryLight}
        );
    }
`;

export const ImageViewerContainer = styled.div<{ $hasImages: boolean }>`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    ${device.lg} {
        flex-basis: ${({ $hasImages }) =>
            $hasImages ? "50%" : "0"}; // Take up 50% when images are available
    }
    width: 100%;
    transition: all 0.3s ease-in-out;
    opacity: ${({ $hasImages }) => ($hasImages ? "1" : "0")};
    .content {
        padding: 1.5rem;
        overflow-y: auto;
        max-height: 100%;
        width: 100%;
    }
`;

export const StyledUploadContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem; /* space-y-4 */
    padding-top: 1rem; /* pt-4 */
`;

// Styled Label
export const UploadLabel = styled(Label)`
    color: ${colourScheme.white}; /* text-[#E8EAF0] */
`;

// Styled Upload Area
export const StyledUploadArea = styled.div`
    border-width: 0.2rem;
    border-style: dashed;
    border-color: ${colourScheme.secondary}; /* border-[#4F80CC] */
    border-radius: 0.5rem; /* rounded-lg */
    padding: 1rem; /* p-4 */
    text-align: center;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
        background-color: ${colourScheme.primaryLight}; /* hover:bg-[#2C2E52] */
    }
`;

// Styled Hidden Input
export const StyledHiddenInput = styled(Input)`
    display: none; /* hidden */
`;

// Styled Upload Text
export const StyledUploadText = styled.p`
    color: ${colourScheme.white}; /* text-[#E8EAF0] */
`;

// Styled Image Preview Container
export const StyledImagePreviewContainer = styled.div`
    position: relative; /* relative */
    width: 8rem; /* w-32 */
    height: 8rem; /* h-32 */
    margin-top: 0.5rem; /* mt-2 */
`;

// Styled Preview Image
export const StyledPreviewImage = styled.img`
    width: 100%; /* w-full */
    height: 100%; /* h-full */
    object-fit: cover; /* object-cover */
    border-radius: 0.375rem; /* rounded-md */
`;

// Styled Delete Button
export const StyledDeleteButton = styled(Button)`
    position: absolute; /* absolute */
    top: 0;
    right: 0;
    background-color: ${colourScheme.error}; /* bg-[#EB4D4B] */
    color: ${colourScheme.fullWhite}; /* text-[#FFFFFF] */
    border-radius: 9999px; /* rounded-full */
    &:hover {
        background-color: ${colourScheme.errorDark}; /* hover:bg-[#D84341] */
    }
`;

export const ScreenWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

export const RowContainer = styled.div`
    display: flex;
    gap: 0.5rem;
    flex-direction: row;
`;

export const InnerContainer = styled.div<{ hasResponses: boolean }>`
    display: flex;
    flex-direction: column;
    width: 100%;
    background-color: ${colourScheme.primaryDark};
    padding: ${({ hasResponses }) => (hasResponses ? "1rem" : "0")};
    box-shadow: ${({ hasResponses }) =>
        hasResponses ? "0 0.5rem 1rem rgba(0, 0, 0, 0.5)" : "0"};
    border-radius: 1rem;
    transition: all 0.5s ease-in-out;

    ${device.lg} {
        flex-direction: row;
    }
`;

export const LoaderContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
`;

export const TextArea = styled.textarea`
    width: 100%;
    height: 8rem;
    background-color: ${colourScheme.primary};
    color: ${colourScheme.white};
    border: 1px solid ${colourScheme.primaryLight};
    border-radius: 0.375rem;
    padding: 0.5rem;
`;

export const Container = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

interface FieldContainerProps {
    $fullWidth?: boolean;
}

export const FieldContainer = styled.div<FieldContainerProps>`
    width: ${({ $fullWidth }) => ($fullWidth ? "100%" : "auto")};
    grid-column: ${({ $fullWidth }) => ($fullWidth ? "1 / -1" : "auto")};
    margin-bottom: 0.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

export const LabelTooltipContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

export const StyledLabel = styled(Label)`
    margin: 0;
`;

export const SwitchContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

export const AlignedFieldContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    width: 100%;
`;

export const StyledCategoryContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem; /* space-y-4 */
    margin-bottom: 1rem;
`;

// Styled Category Grid
export const StyledCategoryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr); /* grid-cols-2 */
    gap: 0.5rem; /* gap-2 */

    @media (min-width: 640px) {
        /* sm:grid-cols-3 */
        grid-template-columns: repeat(3, 1fr);
    }
`;

// Styled Button (for categories)
interface StyledCategoryButtonProps {
    selected: boolean;
}

export const StyledCategoryButton = styled(Button)<StyledCategoryButtonProps>`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 0.5rem; /* space-x-2 */
    height: 2.5rem; /* h-10 */
    padding: 0 0.75rem; /* px-3 */
    border-radius: 0.5rem; /* rounded-lg */
    border: 1px solid ${colourScheme.primary};
    transition: background-color 0.2s;

    background-color: ${(props) =>
        props.selected
            ? colourScheme.secondary
            : colourScheme.primaryLight}; /* bg-[#4F80CC] or bg-[#2C2E52] */
    color: ${(props) =>
        props.selected
            ? colourScheme.fullWhite
            : colourScheme.white}; /* text-[#FFFFFF] or text-[#E8EAF0] */

    &:hover {
        background-color: ${(props) =>
            props.selected
                ? colourScheme.secondaryDark
                : colourScheme.primary}; /* hover:bg-[#3D6EB8] or hover:bg-[#3D4066] */
    }
`;

// Styled Icon (for category buttons)
export const StyledIcon = styled.div`
    width: 1rem; /* w-4 */
    height: 1rem; /* h-4 */
`;

// Styled Category Label
export const StyledCategoryLabel = styled(Label)`
    font-size: 1rem;
`;

// Styled Switch
export const StyledSwitch = styled(Switch)`
    &[data-state="checked"] {
        background-color: ${colourScheme.primary}; /* data-[state=checked]:bg-[#4F80CC] */
    }
`;

// Styled Tabs and TabsList
export const StyledTabs = styled(Tabs)`
    width: 100%;
`;

export const StyledTabsList = styled.div`
    display: grid;
    width: 100%;
    grid-template-columns: repeat(1, 1fr); /* grid-cols-2 */
    background-color: ${colourScheme.darkGradientStart}; /* bg-[#0F1123] */
    border-radius: 0.5rem; /* rounded-lg */
    padding: 0.5rem; /* p-1 */
    margin-bottom: 1rem;
    ${device.md} {
        grid-template-columns: repeat(2, 1fr);
    }
`;

export const StyledTabsTrigger = styled(Button)<{ active?: boolean }>`
    border-radius: 0.375rem; /* rounded-md */
    text-align: center;
    transition:
        background-color 0.2s,
        color 0.2s;
    font-weight: ${(props) => (props.active ? "600" : "normal")};
    padding: 0.5rem;
    font-size: 1rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    background: ${(props) =>
        props.active
            ? `linear-gradient(to right, ${colourScheme.secondary}, ${colourScheme.secondaryLight})` /* bg-gradient-to-r from-[#4F80CC] to-[#7AA8E3] */
            : colourScheme.primary}; /* bg-[#2C2E52] */

    color: ${(props) =>
        props.active
            ? colourScheme.fullWhite
            : colourScheme.grey}; /* text-[#FFFFFF] or text-[#B0B0B3] */

    &:hover {
        background-color: ${(props) =>
            props.active
                ? colourScheme.secondaryLight
                : colourScheme.primaryDark}; /* hover:bg-[#3D4066] */
        color: ${(props) =>
            props.active
                ? colourScheme.white
                : colourScheme.white}; /* hover:text-[#E8EAF0] */
    }
`;

export const StyledTabsTriggerContent = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
`;
